import * as React from 'react';
import {
    Divider,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    ArrayInput,
    AutocompleteInput,
    ReferenceInput,
    SelectInput,
    SimpleFormIterator,
    TextInput,
    email,
    required,
    useCreate,
    useGetIdentity,
    useNotify,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { Avatar } from './Avatar';
import {
    useContactDuplicateCheck,
    DuplicateWarning,
} from '../misc/DuplicateWarning';

export const ContactInputs = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Stack gap={2} p={1}>
            <Avatar />
            <Stack gap={3} direction={isMobile ? 'column' : 'row'}>
                <Stack gap={4} flex={4}>
                    <ContactIdentityInputs />
                    <ContactPositionInputs />
                </Stack>
                <Divider
                    orientation={isMobile ? 'horizontal' : 'vertical'}
                    flexItem
                />
                <Stack gap={4} flex={5}>
                    <ContactPersonalInformationInputs />
                </Stack>
            </Stack>
        </Stack>
    );
};

const ContactIdentityInputs = () => {
    const firstName = useWatch({ name: 'first_name' }) || '';
    const lastName = useWatch({ name: 'last_name' }) || '';
    const emailJsonb = useWatch({ name: 'email_jsonb' });
    const firstEmail = emailJsonb?.[0]?.email || '';
    const duplicateMatches = useContactDuplicateCheck(
        firstName,
        lastName,
        firstEmail
    );

    return (
        <Stack>
            <Typography variant="h6">Identity</Typography>
            <TextInput
                source="first_name"
                validate={required()}
                helperText={false}
            />
            <TextInput
                source="last_name"
                validate={required()}
                helperText={false}
            />
            <DuplicateWarning matches={duplicateMatches} entityType="contact" />
        </Stack>
    );
};

const ContactPositionInputs = () => {
    const [create] = useCreate();
    const { identity } = useGetIdentity();
    const notify = useNotify();
    const handleCreateAccount = async (name?: string) => {
        if (!name) return;
        try {
            const newAccount = await create(
                'accounts',
                {
                    data: {
                        name,
                        sales_id: identity?.id,
                        created_at: new Date().toISOString(),
                    },
                },
                { returnPromise: true }
            );
            return newAccount;
        } catch (error) {
            notify('An error occurred while creating the account', {
                type: 'error',
            });
        }
    };
    return (
        <Stack>
            <Typography variant="h6">Position</Typography>
            <TextInput source="title" helperText={false} />
            <ReferenceInput source="account_id" reference="accounts">
                <AutocompleteInput
                    optionText="name"
                    onCreate={handleCreateAccount}
                    helperText={false}
                />
            </ReferenceInput>
        </Stack>
    );
};

const ContactPersonalInformationInputs = () => {
    const { getValues, setValue } = useFormContext();

    // set first and last name based on email
    const handleEmailChange = (email: string) => {
        const { first_name, last_name } = getValues();
        if (first_name || last_name || !email) return;
        const [first, last] = email.split('@')[0].split('.');
        setValue('first_name', first.charAt(0).toUpperCase() + first.slice(1));
        setValue(
            'last_name',
            last ? last.charAt(0).toUpperCase() + last.slice(1) : ''
        );
    };

    const handleEmailPaste: React.ClipboardEventHandler<HTMLDivElement> = e => {
        const email = e.clipboardData?.getData('text/plain');
        handleEmailChange(email);
    };

    const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const email = e.target.value;
        handleEmailChange(email);
    };

    return (
        <Stack>
            <Typography variant="h6">Personal info</Typography>
            <ArrayInput
                source="email_jsonb"
                label="Email addresses"
                helperText={false}
            >
                <SimpleFormIterator inline disableReordering>
                    <TextInput
                        source="email"
                        helperText={false}
                        validate={email()}
                        onPaste={handleEmailPaste}
                        onBlur={handleEmailBlur}
                    />
                    <SelectInput
                        source="type"
                        helperText={false}
                        optionText="id"
                        choices={personalInfoTypes}
                        defaultValue="Work"
                        fullWidth={false}
                        sx={{ width: 100, minWidth: 100 }}
                    />
                </SimpleFormIterator>
            </ArrayInput>
            <ArrayInput
                source="phone_jsonb"
                label="Phone Number"
                helperText={false}
            >
                <SimpleFormIterator inline disableReordering>
                    <TextInput source="number" helperText={false} />
                    <SelectInput
                        source="type"
                        helperText={false}
                        optionText="id"
                        choices={personalInfoTypes}
                        defaultValue="Work"
                        fullWidth={false}
                        sx={{ width: 100, minWidth: 100 }}
                    />
                </SimpleFormIterator>
            </ArrayInput>
        </Stack>
    );
};

const personalInfoTypes = [{ id: 'Work' }, { id: 'Home' }, { id: 'Other' }];
